// F-077 : paquet de passation et transfert d'un logement vers un autre espace Bailivo.
import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router";
import { post } from "@core/api";
import { useGet, useInvalidate } from "@core/hooks";
import { date, dateTime, money } from "@core/format";
import { PublicShell } from "@core/layout";
import { useOrg } from "@core/org";
import { useSession } from "@core/session";
import { Button, Card, Empty, ErrorBox, Input, Loading, Notice, PageHeader, Select, Table, Textarea, fieldError } from "@core/ui";
import { BackLink, PropertySelect, Status, TRANSFER_KINDS, TRANSFER_STATUS, docUrl, options, orgApi, useAction } from "./shared";

function NotTransferred({ items }: { items: string[] }) {
  return (
    <Card title="Ce qui n’est pas transmis">
      <ul className="small">
        {items.map((x) => (
          <li key={x}>{x}</li>
        ))}
      </ul>
    </Card>
  );
}

/** Accès retirés et conservés à l'acceptation, chacun avec son statut (icône et mot). */
function Access({ access }: { access?: { revoked?: string[]; kept?: string[] } }) {
  const revoked = access?.revoked ?? [];
  const kept = access?.kept ?? [];
  return (
    <Card title="Accès à l’acceptation">
      {revoked.length || kept.length ? (
        <ul className="list-plain stack-sm">
          {revoked.map((x) => (
            <li key={`r-${x}`}>
              <Status value="revoquee" label="Retiré" /> {x}
            </li>
          ))}
          {kept.map((x) => (
            <li key={`k-${x}`}>
              <Status value="active" label="Conservé" /> {x}
            </li>
          ))}
        </ul>
      ) : (
        <p className="muted">Aucun accès relevé.</p>
      )}
    </Card>
  );
}

function Package({ p }: { p: any }) {
  const open: string[] = p.open_points ?? [];
  return (
    <div className="stack">
      <Card title="Baux">
        {p.leases.length ? (
          <Table caption="Baux du logement">
            <thead>
              <tr>
                <th>Statut</th>
                <th>Début</th>
                <th>Locataires</th>
                <th className="right">Loyer</th>
                <th className="right">Dépôt détenu</th>
                <th className="right">Impayés</th>
                <th>Prochaine échéance</th>
              </tr>
            </thead>
            <tbody>
              {p.leases.map((l: any) => (
                <tr key={l.id}>
                  <td>{l.status}</td>
                  <td className="num">{date(l.start_on)}</td>
                  <td className="small">{l.tenants.map((t: any) => t.full_name).join(", ")}</td>
                  <td className="vie-amount">{money(l.rent_cents)}</td>
                  <td className="vie-amount">{money(l.deposit_cents)}</td>
                  <td className="vie-amount">{money(l.unpaid_cents)}</td>
                  <td className="num">{l.next_due_on ? date(l.next_due_on) : "—"}</td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <p className="muted">Aucun bail.</p>
        )}
      </Card>
      <Card title={`Points non résolus (${open.length})`}>
        {open.length ? (
          <ul>
            {open.map((x, i) => (
              <li key={`${i}-${x}`}>{x}</li>
            ))}
          </ul>
        ) : (
          <p className="muted">Aucun point en suspens relevé.</p>
        )}
        <p className="small muted">Impayés, paiements non affectés, dépôt de garantie, bail en signature, congé, incidents, sinistres, demandes, avenants, clés et tâches ouvertes : ils figurent aussi dans la preuve de passation.</p>
      </Card>
      <Access access={p.access} />
      <div className="grid-2">
        <Card title="Équipements">
          {p.equipments.length ? (
            <ul className="list-plain small">
              {p.equipments.map((e: any, i: number) => (
                <li key={i}>
                  {e.name} · prochain entretien {date(e.next_maintenance_on)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Aucun.</p>
          )}
        </Card>
        <Card title="Clés (sans codes)">
          {p.keys.length ? (
            <ul className="list-plain small">
              {p.keys.map((k: any, i: number) => (
                <li key={i}>
                  {k.label} · {k.status}
                  {k.has_code ? " · code à communiquer séparément" : ""}
                </li>
              ))}
            </ul>
          ) : (
            <p className="muted">Aucune.</p>
          )}
        </Card>
      </div>
      <Card title={`Documents (${p.documents.length}) · conversations : ${p.conversations_count}`}>
        <ul className="list-plain small">
          {p.documents.map((d: any) => (
            <li key={d.id}>
              <a href={docUrl(d.id)} target="_blank" rel="noreferrer">
                {d.title || d.filename}
              </a>{" "}
              <span className="muted">· {d.category} · {date(d.created_at)}</span>
            </li>
          ))}
        </ul>
      </Card>
      <NotTransferred items={p.not_transferred} />
    </div>
  );
}

export function HandoverPage() {
  const org = useOrg();
  const [propertyId, setPropertyId] = useState("");
  const pkg = useGet<any>(propertyId ? orgApi(org.orgId, `/properties/${propertyId}/handover`) : null);
  const transfers = useGet<{ items: any[] }>(orgApi(org.orgId, "/transfers"));
  const invalidate = useInvalidate();
  const [form, setForm] = useState({ target_email: "", kind: "vente", effective_on: "", note: "" });
  const act = useAction();
  const owner = org.role === "proprietaire";
  return (
    <div className="page">
      <BackLink to={org.to("departs")}>Départs</BackLink>
      <PageHeader
        eyebrow="Vie du logement"
        title="Passation d’un logement"
        intro="Préparez un dossier de reprise limité au nécessaire, puis, si le repreneur utilise Bailivo, transférez le logement vers son espace. L’historique de votre espace est conservé."
      />
      <ErrorBox error={act.error} />
      <PropertySelect orgId={org.orgId} value={propertyId} onChange={setPropertyId} label="Logement concerné" />
      {pkg.isLoading ? <Loading /> : null}
      {pkg.data ? (
        <>
          <div className="row">
            <Button
              variant="secondaire"
              busy={act.busy}
              onClick={async () => {
                const r: any = await act.run(() => post(orgApi(org.orgId, `/properties/${propertyId}/handover/document`), {}), "Paquet de passation généré.");
                if (r) window.open(docUrl(r.document_id), "_blank");
              }}
            >
              Générer le PDF de passation
            </Button>
          </div>
          <Package p={pkg.data} />
          <Card title="Transférer vers un autre espace Bailivo">
            {owner ? (
              <form
                className="stack-sm"
                onSubmit={async (e) => {
                  e.preventDefault();
                  if (await act.run(() => post(orgApi(org.orgId, `/properties/${propertyId}/transfers`), { ...form, effective_on: form.effective_on || undefined }), "Demande de transfert envoyée au repreneur.")) {
                    invalidate(orgApi(org.orgId, "/transfers"));
                  }
                }}
              >
                <div className="form-row">
                  <Input label="Adresse e-mail du repreneur" type="email" required value={form.target_email} error={fieldError(act.error, "target_email")} onChange={(e) => setForm({ ...form, target_email: e.target.value })} />
                  <Select label="Motif" value={form.kind} options={options(TRANSFER_KINDS)} onChange={(e) => setForm({ ...form, kind: e.target.value })} />
                  <Input label="Date d’effet" type="date" value={form.effective_on} onChange={(e) => setForm({ ...form, effective_on: e.target.value })} />
                </div>
                <Textarea label="Message" rows={2} value={form.note} onChange={(e) => setForm({ ...form, note: e.target.value })} />
                <p className="small muted">
                  Le repreneur accepte depuis son propre espace, en tant que propriétaire. Le logement, ses baux et leurs données (échéances, paiements, pièces, états des lieux, quittances, recettes et dépenses du logement, incidents…) sont alors déplacés en une seule opération ; les éléments listés ci-dessus restent ici. Chaque espace reçoit une preuve de passation.
                </p>
                <div>
                  <Button type="submit" busy={act.busy}>
                    Envoyer la demande de transfert
                  </Button>
                </div>
              </form>
            ) : (
              <p className="muted">Seul un propriétaire de l’espace peut transférer un logement.</p>
            )}
          </Card>
        </>
      ) : null}
      <Card title="Transferts">
        {transfers.data?.items.length ? (
          <Table caption="Transferts">
            <thead>
              <tr>
                <th>Logement</th>
                <th>Sens</th>
                <th>Destinataire</th>
                <th>Date d’effet</th>
                <th>Statut</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {transfers.data.items.map((t: any) => (
                <tr key={t.id}>
                  <td>
                    {t.property_label}
                    <span className="block small muted">
                      {TRANSFER_KINDS[t.kind]} · {dateTime(t.created_at)}
                    </span>
                  </td>
                  <td>{t.direction === "sortant" ? "Sortant" : "Entrant"}</td>
                  <td>{t.target_email}</td>
                  <td className="num">{t.effective_on ? date(t.effective_on) : "—"}</td>
                  <td>
                    <Status value={t.expired ? "expiree" : t.status} labels={{ ...TRANSFER_STATUS, expiree: "Expirée" }} />
                  </td>
                  <td className="stack-sm">
                    {t.package_document_id && t.direction === "sortant" ? (
                      <a className="small" href={docUrl(t.package_document_id)} target="_blank" rel="noreferrer">
                        Paquet
                      </a>
                    ) : null}
                    {t.proof_document_id ? (
                      <a className="small" href={docUrl(t.proof_document_id)} target="_blank" rel="noreferrer">
                        Preuve de passation
                      </a>
                    ) : null}
                    {owner && t.status === "en_attente" && t.direction === "sortant" ? (
                      <Button small variant="discret" onClick={async () => { await act.run(() => post(orgApi(org.orgId, `/transfers/${t.id}/cancel`), {}), "Demande annulée."); invalidate(orgApi(org.orgId, "/transfers")); }}>
                        Annuler
                      </Button>
                    ) : null}
                  </td>
                </tr>
              ))}
            </tbody>
          </Table>
        ) : (
          <Empty title="Aucun transfert" />
        )}
      </Card>
    </div>
  );
}

/** Page du repreneur : lien reçu par courriel. */
export function TransferAcceptPage() {
  const { token = "" } = useParams();
  const nav = useNavigate();
  const { refresh } = useSession();
  const q = useGet<any>(`/api/v1/vie/transfers/${token}`);
  const [target, setTarget] = useState("");
  const act = useAction();
  return (
    <PublicShell wide>
      <h1>Reprise d’un logement</h1>
      {q.isLoading ? <Loading /> : null}
      <ErrorBox error={q.error ?? act.error} />
      {q.data ? (
        <div className="stack">
          <p>
            {q.data.source_org_name} vous propose de reprendre le logement « {q.data.property_label} » ({TRANSFER_KINDS[q.data.kind]}
            {q.data.effective_on ? `, à compter du ${date(q.data.effective_on)}` : ""}).
          </p>
          {q.data.note ? <Notice tone="info">{q.data.note}</Notice> : null}
          <Package p={q.data.summary} />
          {q.data.target_orgs.length ? (
            <Card title="Accepter le transfert">
              <p className="small muted">
                L’acceptation déplace le logement et ses données dans l’espace choisi. Chaque espace reçoit une preuve de passation (date d’effet, accès retirés, éléments transférés, points non résolus), disponible ensuite dans « Passation d’un logement ».
              </p>
              <Select label="Espace qui reprend le logement" value={target} onChange={(e) => setTarget(e.target.value)} options={[{ value: "", label: "Choisir…" }, ...q.data.target_orgs.map((o: any) => ({ value: o.id, label: o.name }))]} />
              <div className="row">
                <Button
                  busy={act.busy}
                  onClick={async () => {
                    const r: any = await act.run(() => post(`/api/v1/vie/transfers/${token}/accept`, { target_org_id: target }), "Logement transféré dans votre espace.");
                    if (r) {
                      await refresh();
                      nav(`/espace/${r.target_org_id}/logements/${r.property_id}`);
                    }
                  }}
                >
                  Accepter et transférer
                </Button>
                <Button
                  variant="secondaire"
                  onClick={async () => {
                    if (await act.run(() => post(`/api/v1/vie/transfers/${token}/refuse`, {}), "Transfert refusé.")) nav("/moi");
                  }}
                >
                  Refuser
                </Button>
              </div>
            </Card>
          ) : (
            <Notice tone="attention">
              Vous n’êtes propriétaire d’aucun espace Bailivo. <Link to="/espaces?nouveau=1">Créez votre espace</Link>, puis revenez sur ce lien.
            </Notice>
          )}
        </div>
      ) : null}
    </PublicShell>
  );
}
